import { FormEvent, useCallback, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { InternalRefetchQueriesInclude } from "@apollo/client";

import { DEFAULT_PROPERTIES_RADIUS } from "dash-common";
import {
  Form,
  ModalContext,
  Text,
  Flex,
  FormContext,
  toast,
  useServerResponseTranslation,
  Drawer,
  Box,
  Button,
  ViewerContext,
} from "dash-ui";

import NameSection from "containers/alerts/AddAlertModal/components/NameSection/NameSection";
import PriceSection from "containers/alerts/AddAlertModal/components/PriceSection/PriceSection";
import PropertyOptionsSection from "containers/alerts/AddAlertModal/components/PropertyOptionsSection/PropertyOptionsSection";
import LocationSection from "containers/alerts/AddAlertModal/components/LocationSection/LocationSection";
import { FormFields } from "containers/alerts/AddAlertModal/constants/constants";
import useValidation from "containers/alerts/AddAlertModal/hooks/useValidation";
import {
  GetAlertConfigsDocument,
  CurrentBrokerHasAlertConfigsDocument,
  useCreateAlertConfigMutation,
  useUpdateAlertConfigMutation,
  useGetConfigAlertByIdQuery,
} from "gql/generated.graphql";

import AdditionalPropertyOptionsSection from "./components/AdditionalPropertyOptionsSection/AdditionalPropertyOptionsSection";
import { getAlertByIdDocumentToFormData } from "./AddAlertModal.helpers";
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from "common/google-tag-manager/helpers";
import { GTMEventsEnum } from "common/google-tag-manager/constants";
import { buildBuiltRange, buildRangeString } from "common/helpers";
import MarketSection from "containers/alerts/AddAlertModal/components/MarketSelect/MarketSection";
import { useCheckFreeLimitedUser } from "common/hooks/use-check-free-limited-user/use-check-free-limited-user";

const AddAlertModal = () => {
  const { account, broker } = useContext(ViewerContext);
  const { isFreeLimitedUser } = useCheckFreeLimitedUser();
  const theme = useTheme();
  const { t } = useTranslation(["common", "pages"]);
  const { isOpenCurrent, toggleModal, payload, setPayload } =
    useContext(ModalContext);
  const isOpen = isOpenCurrent["AddAlertModal"];
  const formRef = useRef<HTMLFormElement | null>(null);
  const { formState, setFormValues, resetFormErrors, resetForm } =
    useContext(FormContext);
  const valid = useValidation();
  const translateErrorMessage = useServerResponseTranslation();
  const isEditMode = Boolean(payload?.alertConfigId);

  const { data: alertConfigItem, loading: isGetConfigAlertByIdLoading } =
    useGetConfigAlertByIdQuery({
      variables: { alertConfigId: payload?.alertConfigId },
      skip: !isEditMode,
    });
  const [createAlertConfigMutation, { loading: isCreateLoading }] =
    useCreateAlertConfigMutation();
  const [updateAlertConfigMutation, { loading: isUpdateLoading }] =
    useUpdateAlertConfigMutation();
  const loading = isCreateLoading || isUpdateLoading;

  const closeModal = () => {
    toggleModal(false, "AddAlertModal");
    setPayload({});
    resetForm();
  };

  const onBackButtonClick = useCallback(() => {
    const { paginationState } = payload || {};
    closeModal();
    toggleModal(true, "AlertListModal");
    setPayload({ paginationState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const onDeleteButtonClick = useCallback(() => {
    const { alertConfigId, paginationState } = payload || {};
    closeModal();
    toggleModal(true, "RemoveAlertModal");
    setPayload({ alertConfigId, paginationState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const {
      name,
      place,
      radius,
      min,
      max,
      buildingTypes,
      numberOfBedrooms,
      numberOfBathrooms,
      numberOfUnits,
      featuresAndAmenities,
      projectStatus,
      yearBuilt,
      markets,
    } = formState;
    if (
      !valid(FormFields.Name, name) ||
      !valid(FormFields.MinPropertyPrice, min) ||
      !valid(FormFields.MaxPropertyPrice, max)
    ) {
      return null;
    }

    const input = {
      name,
      filter: {
        buildingTypes: buildingTypes,
        ...(place && {
          locationFilter: {
            lat: place.lat,
            lng: place.lng,
            radiusToSearchInMiles: radius,
          },
        }),
        projectStatuses: projectStatus,
        amenityIds: featuresAndAmenities,
        yearBuildRange:
          yearBuilt?.min && yearBuilt?.max ? yearBuilt : undefined,
        unitNumberOfBathrooms: numberOfBathrooms
          ? {
              min: numberOfBathrooms,
              max: numberOfBathrooms,
            }
          : null,
        unitNumberOfBedrooms: numberOfBedrooms
          ? {
              min: numberOfBedrooms,
              max: numberOfBedrooms,
            }
          : null,
        unitNumberOfUnits: numberOfUnits,
        priceRange: {
          min,
          max: max === "Any Price" ? null : max,
        },
        marketAreaIds: markets,
      },
    };
    const refetchQueries: InternalRefetchQueriesInclude = [
      CurrentBrokerHasAlertConfigsDocument,
      {
        query: GetAlertConfigsDocument,
        variables: {
          pagination: {
            limit: 10,
          },
        },
        fetchPolicy: "network-only",
      },
    ];

    if (!payload?.alertConfigId) {
      createAlertConfigMutation({
        variables: input,
        ...(broker && refetchQueries),
        onCompleted: () => {
          toast.success(t("pages:alertPage.toasts.alertIsCreated"));
          if (account) {
            createGTMEvent({
              dataLayer: {
                event: GTMEventsEnum.ALERT_CREATED,
                user_id: getGoogleTagManagerUserId(account.email),
                alert_name: input.name,
                price_range: buildRangeString(input.filter?.priceRange),
                property_type: input.filter?.buildingTypes?.join(","),
                number_of_bedrooms: buildRangeString(
                  input.filter?.unitNumberOfBedrooms
                ),
                number_of_bathrooms: buildRangeString(
                  input.filter?.unitNumberOfBathrooms
                ),
                number_of_units: buildRangeString(
                  input.filter?.unitNumberOfUnits
                ),
                features_and_amenities: input.filter?.amenities?.join(","),
                project_status: input.filter?.projectStatuses?.join(","),
                year_built: buildBuiltRange({
                  startDate: `${yearBuilt?.min}`,
                  endDate: `${yearBuilt?.max}`,
                  yearOnly: true,
                }),
                address: place?.defaultValue,
                radius: radius,
              },
            });
          }
          closeModal();
        },
        onError: (error) =>
          toast.error(
            translateErrorMessage(error, ["common:brokerPortalServerErrors"])
          ),
      });
    } else {
      updateAlertConfigMutation({
        variables: { ...input, alertConfigId: payload.alertConfigId },
        refetchQueries,
        onCompleted: () => {
          toast.success(t("pages:alertPage.toasts.alertIsUpdated"));
          closeModal();
        },
        onError: (error) =>
          toast.error(
            translateErrorMessage(error, ["common:brokerPortalServerErrors"])
          ),
      });
    }
  };

  useEffect(() => {
    const { advancedFilterDrawerData = {}, alertConfigId } = payload || {};

    if (isOpen && alertConfigId) {
      if (!isGetConfigAlertByIdLoading) {
        const alertConfigData = getAlertByIdDocumentToFormData(
          // @ts-ignore
          alertConfigItem.getAlertConfigById
        );
        setFormValues(alertConfigData);
      }
    } else {
      setFormValues({
        ...advancedFilterDrawerData,
        radius: advancedFilterDrawerData.radius || DEFAULT_PROPERTIES_RADIUS,
      });
    }

    resetFormErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, alertConfigItem, isGetConfigAlertByIdLoading, isOpen]);

  return (
    <Drawer
      isOpen={isOpen}
      onRequestClose={closeModal}
      title={
        (!isEditMode && t("pages:alertPage.labels:createAlertTitle")) || ""
      }
      backButton={
        isEditMode
          ? {
              children: t("pages:alertPage.labels:backButtonLabel") || "",
              onClick: onBackButtonClick,
              medium: true,
              pl: 0,
            }
          : undefined
      }
      submitButton={{
        title: isEditMode ? t("common:labels.save") : t("common:labels.create"),
        onClick: () => {
          if (isFreeLimitedUser) {
            closeModal();
            toggleModal(true, "UpgradeSubscriptionModal");
            return;
          }
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
        isLoading: loading,
        disabled: loading,
      }}
      cancelButton={{
        title: t("common:labels.cancel"),
        onClick: closeModal,
      }}
    >
      <Form ref={formRef} onSubmit={onFormSubmit}>
        <Box maxWidth={330} mb={4}>
          {!isEditMode && (
            <Text color={theme.colors.n70}>
              {t("pages:alertPage.labels:createAlertSubtitle")}
            </Text>
          )}
          {isEditMode && (
            <Text lg semibold color={theme.colors.n90}>
              {t("pages:alertPage.labels:alertDetails")}
            </Text>
          )}
        </Box>
        <Flex justifyContent="space-between">
          <Text>{t("pages:alertPage.labels.alertInfo")}</Text>

          {isEditMode && (
            <Button
              onClick={onDeleteButtonClick}
              small
              variation="outline"
              colorVariation="danger"
            >
              {t("pages:alertPage.labels:deleteAlertModalTitle")}
            </Button>
          )}
        </Flex>
        <NameSection />
        <Flex mt={4}>
          <Text>{t("pages:alertPage.labels:addAlertSelectFilter")}</Text>
        </Flex>
        <MarketSection />
        <PriceSection />
        <PropertyOptionsSection />
        <AdditionalPropertyOptionsSection />
        <LocationSection />
      </Form>
    </Drawer>
  );
};

export default AddAlertModal;
